import { makeStyles } from "@mui/styles";
import { THEME_COLORS } from "../../context/constants";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column" as const,
    minHeight: "80vh",
    fontFamily: "Lexend, sans-serif",
    padding: "32px 40px",
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
    gap: "16px",
    flexWrap: "wrap" as const,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  titleIcon: {
    color: THEME_COLORS.yellow,
    fontSize: "32px",
  },
  title: {
    fontSize: "28px !important",
    fontWeight: "600 !important" as any,
    color: "#222",
    fontFamily: "Lexend, sans-serif !important",
  },
  subtitle: {
    fontSize: "14px !important",
    color: "#888 !important",
    fontFamily: "Lexend, sans-serif !important",
    marginBottom: "24px !important",
  },
  createButton: {
    backgroundColor: `${THEME_COLORS.yellow} !important`,
    color: "#000 !important",
    fontFamily: "Lexend, sans-serif !important",
    fontWeight: "600 !important",
    borderRadius: "10px !important",
    padding: "8px 20px !important",
    fontSize: "14px !important",
    textTransform: "none !important" as "none",
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor: `${THEME_COLORS.darkYellow} !important`,
      boxShadow: "none !important",
    },
    "&:disabled": {
      opacity: "0.5 !important",
    },
  },
  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    backgroundColor: "#fafafa",
    border: "1px solid #e8e8e8",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "360px",
    marginBottom: "20px",
    "&:focus-within": {
      borderColor: THEME_COLORS.yellow,
      backgroundColor: "#fff",
    },
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontFamily: "Lexend, sans-serif",
    fontSize: "14px",
    backgroundColor: "transparent",
    flex: 1,
    "&::placeholder": {
      color: "#bbb",
    },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
    width: "100%",
  },
  voiceCard: {
    cursor: "default",
    border: "1px solid #e0e0e0",
    borderRadius: "14px",
    padding: "18px",
    backgroundColor: "#fff",
    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      borderColor: THEME_COLORS.yellow,
    },
  },
  voiceCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  voiceName: {
    fontSize: "16px !important",
    fontWeight: "600 !important" as any,
    color: "#222 !important",
    fontFamily: "Lexend, sans-serif !important",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  voiceActions: {
    display: "flex",
    gap: "4px",
    flexShrink: 0,
  },
  playButton: {
    backgroundColor: `${THEME_COLORS.lightYellow3} !important`,
    color: `${THEME_COLORS.darkYellow} !important`,
    width: "32px !important",
    height: "32px !important",
    "&:hover": {
      backgroundColor: `${THEME_COLORS.lightYellow} !important`,
    },
  },
  deleteButton: {
    color: "#bbb !important",
    width: "32px !important",
    height: "32px !important",
    "&:hover": {
      color: "#e74c3c !important",
      backgroundColor: "#fdf0ef !important",
    },
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "6px",
  },
  chip: {
    fontSize: "11px !important",
    height: "22px !important",
    backgroundColor: "#f5f5f5 !important",
    color: "#666 !important",
    fontFamily: "Lexend, sans-serif !important",
    fontWeight: "500 !important",
    textTransform: "capitalize" as const,
  },
  promptPreview: {
    fontSize: "12px !important",
    color: "#888 !important",
    fontFamily: "Lexend, sans-serif !important",
    fontStyle: "italic",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
    lineHeight: "1.4",
  },
  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "64px 24px",
    backgroundColor: "#fafafa",
    border: "1px dashed #ddd",
    borderRadius: "16px",
    marginTop: "24px",
  },
  emptyIconWrapper: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: THEME_COLORS.lightYellow3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyIcon: {
    color: THEME_COLORS.yellow,
    fontSize: "36px !important",
  },
  emptyTitle: {
    fontSize: "18px !important",
    fontWeight: "600 !important" as any,
    color: "#444 !important",
    fontFamily: "Lexend, sans-serif !important",
  },
  emptySubtitle: {
    fontSize: "14px !important",
    color: "#888 !important",
    fontFamily: "Lexend, sans-serif !important",
    textAlign: "center" as const,
    maxWidth: "400px",
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "64px 0",
  },
  // Dialog
  dialogTitle: {
    fontSize: "20px !important",
    fontWeight: "600 !important" as any,
    fontFamily: "Lexend, sans-serif !important",
    display: "flex !important",
    alignItems: "center",
    gap: "10px",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    paddingTop: "8px !important",
  },
  fieldLabel: {
    fontSize: "12px !important",
    fontWeight: "600 !important" as any,
    color: "#666 !important",
    fontFamily: "Lexend, sans-serif !important",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: "4px !important",
  },
  textArea: {
    "& .MuiOutlinedInput-root": {
      fontFamily: "Lexend, sans-serif",
      borderRadius: "12px",
      backgroundColor: "#fafafa",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: THEME_COLORS.yellow,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: THEME_COLORS.yellow,
        borderWidth: "1px",
      },
    },
  },
  inlineRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap" as const,
  },
  inlineField: {
    flex: 1,
    minWidth: "140px",
  },
  resultsSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    marginTop: "12px",
  },
  resultsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultsTitle: {
    fontSize: "14px !important",
    fontWeight: "600 !important" as any,
    color: "#444 !important",
    fontFamily: "Lexend, sans-serif !important",
  },
  resultCard: {
    border: "1px solid #e8e8e8",
    borderRadius: "12px",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "#fff",
    transition: "border-color 0.15s",
    "&:hover": {
      borderColor: THEME_COLORS.yellow,
    },
  },
  resultInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
    minWidth: 0,
  },
  resultName: {
    fontSize: "14px !important",
    fontWeight: "600 !important" as any,
    color: "#222 !important",
    fontFamily: "Lexend, sans-serif !important",
  },
  resultMeta: {
    fontSize: "12px !important",
    color: "#888 !important",
    fontFamily: "Lexend, sans-serif !important",
  },
}));

export default useStyles;
